import type { ReactNode } from 'react';
import { FormMessage } from './FormMessage';
import { useFieldA11y } from './fieldA11y';

interface RadioGroupOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  legend: ReactNode;
  name: string;
  value: string;
  options: RadioGroupOption[];
  onChange: (value: string) => void;
  helperText?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
}

export const RadioGroup = ({
  legend,
  name,
  value,
  options,
  onChange,
  helperText,
  error,
  className,
  disabled = false,
}: RadioGroupProps) => {
  const { fieldId, helperId, errorId, describedByIds } = useFieldA11y({
    helperText,
    error,
  });

  return (
    <fieldset
      className={[
        'form-radio-group',
        error ? 'form-radio-group--invalid' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-describedby={describedByIds}
    >
      <legend className="form-field__label">{legend}</legend>
      <div className="form-radio-group__options">
        {options.map((option) => {
          const optionId = `${fieldId}-${option.value}`;

          return (
            <label className="form-radio-group__option" htmlFor={optionId} key={option.value}>
              <input
                id={optionId}
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                disabled={disabled || option.disabled}
                onChange={() => onChange(option.value)}
              />
              <span>
                <strong>{option.label}</strong>
                {option.description && <small>{option.description}</small>}
              </span>
            </label>
          );
        })}
      </div>
      <FormMessage id={helperId}>{helperText}</FormMessage>
      <FormMessage id={errorId} tone="error">
        {error}
      </FormMessage>
    </fieldset>
  );
};
